import BlockIcon from "@mui/icons-material/Block";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutlined";
import { Box, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../components/UIToolkit/Badges/MetaChip";
import { InfoIconWithTooltip } from "../../../components/UIToolkit/molecules/InfoIconWithTooltip";
import { ICON_SIZE } from "../../../theme/tokens";
import type { PbjPlanActionProgress } from "../../../utils/models";
import { formatCurrency, formatWholeNumber } from "./planValue.utils";

type Props = {
	action: PbjPlanActionProgress;
	/** Whether the on-track verdict carries information yet — see `isActionJudged`. */
	judged: boolean;
	/** The lever's delivered figure, already printed in its direction with its unit. */
	deliveredText: string;
};

const INFO_ICON_SX = { fontSize: ICON_SIZE.PILL } as const;

/** The verdict chip and the delivered / points / money figures on one wrapping line under an action's title. */
export const PlanActionStatusLine: FC<Props> = ({ action, judged, deliveredText }) => {
	const { t } = useTranslation();
	const isCut = action.direction === "cut";
	// A cut's `goalPoints` is what it gives up (≤ 0). One that stays above every floor loses nothing,
	// and says so in words rather than "0 pts given up". Rounded before the sign is read.
	const pointsGivenUp = Math.round(-action.goalPoints);
	const cutPointsGivenUp = isCut && pointsGivenUp > 0;
	const pointsCopy = !isCut
		? t("pbj_model.tracker.action_points", { value: formatWholeNumber(action.goalPoints) })
		: cutPointsGivenUp
			? t("pbj_model.tracker.action_points_at_risk", { value: `${pointsGivenUp}`, count: pointsGivenUp })
			: t("pbj_model.recommendations.no_points_lost");

	return (
		<Stack direction="row" spacing={0.75} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 0.5, mt: 0.5 }}>
			{/* An infeasible lever is neither on nor off track — it cannot be measured at all,
			    so it gets its own read rather than a red "behind" badge over 0%. */}
			{!action.feasible ? (
				<MetaChip
					tone="warning"
					icon={<BlockIcon />}
					label={t("pbj_model.tracker.infeasible")}
					tooltip={t("pbj_model.tracker.infeasible_tooltip")}
					dataTestId={`pbj-model-plan-action-status-${action.id}`}
				/>
			) : judged ? (
				<MetaChip
					tone={action.onTrack ? "success" : "error"}
					icon={action.onTrack ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
					label={t(action.onTrack ? "pbj_model.tracker.on_track" : "pbj_model.tracker.behind")}
					dataTestId={`pbj-model-plan-action-status-${action.id}`}
				/>
			) : (
				// Vacuously true, so withheld — see `isActionJudged`. This is what stops "On track"
				// appearing beside a forecast that lands thousands of hours short of the goal.
				<MetaChip
					tone="neutral"
					icon={<RemoveCircleOutlineIcon />}
					label={t("pbj_model.tracker.not_judged_yet")}
					tooltip={t("pbj_model.tracker.not_judged_yet_tooltip")}
					dataTestId={`pbj-model-plan-action-status-${action.id}`}
				/>
			)}
			{/* `delivered` counts COMPLETED weeks only — it is what the badge above is judged
			    on, and differs from the bar (every elapsed day) by up to six days of hours.
			    The ⓘ carries that explanation now that the per-card date line is gone. */}
			<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
				<Typography variant="caption" sx={{ color: "text.secondary" }} data-testid={`pbj-model-plan-action-delivered-${action.id}`}>
					{t(isCut ? "pbj_model.tracker.total_cut" : "pbj_model.tracker.total_added", { value: deliveredText })}
				</Typography>
				<InfoIconWithTooltip
					title={t(isCut ? "pbj_model.tracker.delivered_tooltip_cut" : "pbj_model.tracker.delivered_tooltip", {
						covered: action.days.covered,
						elapsed: action.days.elapsed,
					})}
					iconSx={INFO_ICON_SX}
					dataTestId={`pbj-model-plan-action-delivered-info-${action.id}`}
				/>
			</Box>
			{/* `goalPoints`, NOT the stored `points`. The stored figure is the marginal gain the
			    Action Plan credited at save time, scored over the Prediction tab's window and
			    frozen; this one is the live ladder walk the markers on this very bar are drawn
			    from. Showing the frozen one here put "0 pts committed" beside a ladder whose
			    cheapest edge the commitment clears — read as a bug, not as two measurements. */}
			<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
				<Typography
					variant="caption"
					// `sx`, not the `color` prop: MUI's Typography only honours bare palette keys there
					// ("warning"), so a dotted path like "warning.dark" silently rendered in body ink.
					sx={cutPointsGivenUp ? { fontWeight: 600, color: "warning.dark" } : { color: "text.disabled" }}
					data-testid={`pbj-model-plan-action-points-${action.id}`}
				>
					{pointsCopy}
				</Typography>
				<InfoIconWithTooltip
					title={t(isCut ? "pbj_model.tracker.action_points_cut_tooltip" : "pbj_model.tracker.action_points_tooltip")}
					iconSx={INFO_ICON_SX}
					dataTestId={`pbj-model-plan-action-points-info-${action.id}`}
				/>
			</Box>
			{/* Per-lever money has no home in the mock, but the endpoint ships it and the plan-level
			    panel only totals it — keeping it here preserves the breakdown. A cut's price is its full
			    savings (negative), so its money line is what it has saved. Any spend it ran up by rising
			    above baseline is named by the note under the bar and counted on the plan's cost panel. */}
			<Typography variant="caption" sx={{ color: "text.disabled" }} data-testid={`pbj-model-plan-action-spend-${action.id}`}>
				{isCut
					? t("pbj_model.tracker.action_savings", { saved: formatCurrency(action.savingsSoFar), savings: formatCurrency(-action.price) })
					: t("pbj_model.tracker.action_spend", { spent: formatCurrency(action.spendSoFar), price: formatCurrency(action.price) })}
			</Typography>
		</Stack>
	);
};
